import { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { useFactsheetsFilters} from './hooks/useFactsheetsFilters'

import GET_FACTSHEETS from "../../../graphql/queries/IIAs/getFactsheets";

import TableSkeleton from "../../../components/Table/components/TableSkeleton";
import CustomTable from "../../../components/Table";
import Filters from "../../../components/Filters";
import ExportFactsheetPdf from "./ExportFactsheetPdf";

import './index.scss'

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
]

const FactsheetBrowser = () => {
    const initialState = {
        erasmusCode: "",
        autumnTermDeadline: "",
        springTermDeadline: "",
        sortBy: localStorage.getItem('sortByFactsheets') !== null ? JSON.parse(localStorage.getItem('sortByFactsheets')) : "changed_descending",
        selectedFiltersList: localStorage.getItem('selectedFiltersFactsheets') !== null ? JSON.parse(localStorage.getItem('selectedFiltersFactsheets')) : {
            erasmusCode: "",
            autumnTermDeadline: "",
            springTermDeadline: "",
        },
    };

    const filters = useFactsheetsFilters(initialState);
    const {
      state,
      setSortBy,
      setSelectedFiltersList,
      setExpandFilters,
      updateMultipleValues
    } = filters;

    const {
      sortBy,
      erasmusCode,
      autumnTermDeadline,
      springTermDeadline,
      selectedFiltersList,
      expandFilters,
    } = state;

    const handleExpand = (value) => {
        setExpandFilters(value);
    };

    const { t } = useTranslation('translation');

    const navigate = useNavigate();

    const [page, setPage] = useState(localStorage.getItem('pageFactsheets') !== null ? parseInt(JSON.parse(localStorage.getItem('pageFactsheets'))) : 0);

    const [factsheets, setFactsheets] = useState([]);
    
    const { loading, error, data, refetch: refetchFactsheets } = useQuery(GET_FACTSHEETS, {notifyOnNetworkStatusChange: true, fetchPolicy: 'network-only', 
        variables: { 
            page: page,
            pageSize: 30,
            optionalFilters: {
                eCode: erasmusCode,
                studentNomAutumnTerm: autumnTermDeadline,
                studentNomSpringTerm: springTermDeadline
            },
            sortBy: sortBy
        }, onCompleted: (data) => {
            setFactsheets(data?.factsheets?.factsheets)
        }
    });

    useEffect(() => {
        const data = localStorage.getItem('selectedFiltersFactsheets');
        
        if (data) {
            if (!Object.values(JSON.parse(data) || {})?.every(x => x === '')) {
                setSelectedFiltersList(JSON.parse(data));
                refetchFactsheets();
            }
        }
    }, []);

    useEffect(() => {
        page !== null && localStorage.setItem('pageFactsheets', JSON.stringify(page));
    }, [page]);

    useEffect(() => {
        localStorage.setItem('selectedFiltersFactsheets', JSON.stringify(selectedFiltersList));
    }, [selectedFiltersList]);

    useEffect(() => {
        sortBy !== null && localStorage.setItem('sortByFactsheets', JSON.stringify(sortBy));
    }, [sortBy]);

    useEffect(() => {
        updateMultipleValues(selectedFiltersList);
        refetchFactsheets();
    }, [
        selectedFiltersList,
        updateMultipleValues,
        refetchFactsheets,
    ]);

    const handleRemoveFilter = (filter, value) => {
        let temp = Object.assign({}, selectedFiltersList);
        temp[filter] = "";
        setSelectedFiltersList(temp);
    };

    const formatDate = (string) => {
        if (!string) return "";
        const [month, day] = string.replace("--", "").split("-");
        const displayDay = parseInt(day);
        const displayMonth = months[parseInt(month) - 1];
        return displayDay && displayMonth ? `${displayDay} ${displayMonth}` : ""
    }
    
    if (loading) {
        return (
        <TableSkeleton
            columnsPerFieldMap={{
                erasmusCode: "col-2",
                institutionName: "col-3",
                country: "col-1",
                autumnTerm: "col-1",
                springTerm: "col-1",
            }}
        />
        );
    }

    if (error)
        return (
        <div className="text-center text-danger mx-auto">{t('errorMessage')}</div>
        );

    return (
        <>
        <main className="h-auto text-center mt-4">
            <CustomTable
                page={page}
                setPage={(p) => setPage(p)}
                pageLimit={30}
                totalCount={data?.factsheets?.totalCount}
                hasPagination={true}
                hasFilters={true}
                expandFilters={expandFilters}
                setExpandFilters={(e) => setExpandFilters(e)}
                filters={selectedFiltersList}
                setFilters={(filter, value) => {handleRemoveFilter(filter, value); setPage(0);}}
                sortBy={sortBy}
                setSortBy={() => setSortBy("")}
                type={"factsheets"}
                customTitle={"Total factsheets"}
                columnsPerFieldMap={{
                    erasmusCode: "col-2",
                    institutionName: "col-3",
                    country: "col-1",
                    autumnTerm: "col-1",
                    springTerm: "col-1",
                }}
                rowClassName="reusable-components-table-row-factsheets"
                headerClassName="reusable-components-table-header-factsheets"
                data={factsheets.map((item) => {
                        return ({
                            erasmusCode: item?.institution?.erasmusCode,
                            institutionName: item?.institution?.name,
                            country: item?.institution?.country,
                            autumnTerm: formatDate(item?.calendar?.studentNomAutumnTerm),
                            springTerm: formatDate(item?.calendar?.studentNomSpringTerm),
                        })
                    })
                }
                onClickHandlers={factsheets.map(({ heiID }) => (e) => {navigate("/factsheets/single-view/" + heiID);})}
            >
                {factsheets?.map((factsheet, index) => (
                  <div
                    key={index}
                    className={`py-3 px-2 justify-content-end justify-content-lg-around align-items-center bg-none bg-md-light button-triplet-requests flex-grow-1 d-flex gap-3 gap-lg-1`}
                  >
                    <ExportFactsheetPdf heiId={factsheet?.heiID} />
                  </div>
                ))}
            </CustomTable>
            <Filters
                expand={expandFilters}
                handleExpand={handleExpand}
                selectedFiltersList={selectedFiltersList}
                setSelectedFiltersList={(e) => {setSelectedFiltersList(e); setPage(0);}}
                sortBy={sortBy}
                setSortBy={(e) => setSortBy(e)}
                type={"factsheets"}
            />
            </main>
        </>
    );
}

export default FactsheetBrowser;